import { Component } from '@angular/core';
import { PrestationModel } from 'src/app/core/models/prestation.model';
import { PrestationService } from './prestation.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PrestationCreateUpdateDeleteComponent } from './prestation-create-update-delete/prestation-create-update-delete.component';
import { FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { PRESTATION_FILTER_SCHEMA } from './prestation-filter/prestation-filter.schema';
import { CrudModeEnum } from 'src/app/core/base/enum/crud-mode.enum';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { ProductModel } from '../../../../core/models/product.model';
import { Router } from '@angular/router';
import { TvaModel } from '../../../../core/models/tva.model';
import { TvaService } from '../../settings/tva/tva.service';

@Component({
  selector: 'vex-prestation',
  templateUrl: './prestation.component.html',
  styleUrls: ['./prestation.component.scss'],
})
export class PrestationComponent extends DataTableBase<PrestationModel> {
  ENUM_PERMISSION = ENUM_PERMISSIONS;
  protected override filterSchema = PRESTATION_FILTER_SCHEMA;

  feisColumns: Array<FeiColumn> = [
    { label: 'Type', column: 'quoteId', type: 'custom' },
    {
      label: 'Réf. Fournisseur',
      column: 'reference',
      propertyDisplay: null,
      canUpdate: false,
      canDelete: false,
      type: 'custom',
    },
    { label: 'Fournisseurs', column: 'supplier', propertyDisplay: 'supplier.label', width: '20%', wrap: true },
    {
      label: 'Prestation générique',
      column: 'genericPrestation',
      propertyDisplay: 'genericPrestation.label',
      canUpdate: false,
      canDelete: false,
    },
    {
      label: 'Libellé',
      column: 'label',
      propertyDisplay: null,
      canUpdate: false,
      canDelete: false,
      type: 'custom',
      width: '300px',
      wrap: true,
    },
    { label: 'Prix H.T.', column: 'priceHT', additionalDisplay: ' €', type: 'number' },
    { label: 'Taux de TVA', column: 'tva', type: 'custom' },
    { label: 'Prix T.T.C.', column: 'priceTTC', additionalDisplay: ' €', type: 'number' },
    {
      label: "Unité d'achat",
      column: 'purchaseUnit',
      propertyDisplay: null,
    },
    {
      label: "Nombre d'interventions/heures",
      column: 'purchaseValue',
      propertyDisplay: null,
    },
    { label: 'Visibilité e-Shop', column: 'isSuspended', type: 'eshopStatus' },
    // { label: 'Catégorie', column: 'category', propertyDisplay: 'category.label', type: 'category' },
    //     {label: '', column: 'actions', canUpdate : this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_FM_REF_PURCHASE_SERVICE_INFO)}
    // PRED-3230 : la suspension e-Shop est une modification de la prestation.
    {
      label: '',
      column: 'actions',
      canArchive: false,
      canUpdate: false,
      canSuspend: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_SERVICE),
    },
  ];

  canShowArchive = true;
  actionText = 'Expiré';
  showComment;
  showPreviousReference;

  constructor(
    public service: PrestationService,
    private dialog: MatDialog,
    private router: Router,
    public authService: AuthService,
    private tvaService: TvaService,
  ) {
    super();
    this.displayedColumns = this.feisColumns.map(x => x.column);
  }

  /**
   * Flags techniques du fetch, hors `this.filters` : jamais dans l'URL ni effacés par le sidenav.
   * `isExpired` suit la case Expirés du header (l'ancien `isExpired: false` codé en dur la neutralisait).
   */
  protected override buildFilters(): Record<string, unknown> {
    return { ...super.buildFilters(), isWithRecurrence: true, isExpired: this.isExpired };
  }

  protected override transformData(data: PrestationModel[]): PrestationModel[] {
    return data.map((entity: any) => ({ ...entity, quoteId: entity.quoteId ?? null }));
  }

  createItem(): void {
    this.dialog
      .open(PrestationCreateUpdateDeleteComponent)
      .afterClosed()
      .subscribe((prestation: PrestationModel) => {
        if (prestation) {
          this.initData();
        }
      });
  }

  hasPermission() {
    return this.authService.isReferentEngagement();
  }

  updateItem(element: PrestationModel): void {
    this.dialog
      .open(PrestationCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((prestation: PrestationModel) => {
        if (prestation) {
          this.initData();
        }
      });
  }

  rowClickItem(element: PrestationModel): void {
    this.readItemForUserPredik(element);
  }

  readItemForUserPredik(element: PrestationModel): void {
    this.dialog
      .open(PrestationCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
          userPredikMode: true,
        },
      })
      .afterClosed()
      .subscribe((prestation: PrestationModel) => {
        if (prestation) {
          this.initData();
        }
      });
  }

  deleteItem(element: PrestationModel) {
    this.dialog
      .open(PrestationCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.technicalIdentifier}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe((prestation: PrestationModel) => {
        if (prestation) {
          this.initData();
        }
      });
  }

  archiveItem(element: PrestationModel) {
    this.dialog
      .open(PrestationCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.technicalIdentifier}`,
          mode: CrudModeEnum.Archive,
        },
      })
      .afterClosed()
      .subscribe((prestation: PrestationModel) => {
        if (prestation) {
          this.initData();
        }
      });
  }

  suspendItem(element: PrestationModel) {
    const call$ = element.isSuspended ? this.service.unsuspend(element) : this.service.suspend(element);
    call$.subscribe(() => this.initData());
  }

  readItem(element: any) {
    this.dialog
      .open(PrestationCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.reference}`,
          mode: CrudModeEnum.Read,
        },
      })
      .afterClosed()
      .subscribe((prestation: PrestationModel) => {
        if (prestation) {
          this.initData();
        }
      });
  }

  importItem() {
    this.dialog
      .open(PrestationCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Import,
        },
      })
      .afterClosed()
      .subscribe((prestation: PrestationModel) => {
        if (prestation) {
          this.initData();
        }
      });
  }

  getTvaRate(tva: TvaModel) {
    return `${this.tvaService.getRateByDate(tva).rate.toFixed(2)}%`;
  }

  redirectToElement(element: ProductModel) {
    if (element.quoteId) {
      this.router.navigateByUrl('/achat/quote/' + element.quoteId).then();
      return;
    } else {
      this.router.navigateByUrl('/management/engagement/' + element.contractId).then();
    }
  }
}
